/**
 * Utility functions for Leave Management API parity with mobile app
 */

/**
 * Checks if absence type indicates RMDDA stores discharge date (e.g. WFHL, WRILL)
 */
export function absenceTypeIndicatesRmddaDischarge(
  absenceTypeCode?: string | null,
  absenceTypeName?: string | null
): boolean {
  const code = String(absenceTypeCode ?? '').toUpperCase().trim();
  const compact = String(absenceTypeName ?? '').replace(/\s+/g, '').toUpperCase();
  return (
    code === 'WRILL' ||
    compact === 'WRILL' ||
    compact === 'WRIIL' ||
    code === 'WFHL' ||
    compact === 'WFHL'
  );
}

/**
 * Matches Leave apply WFHL translated label plus WRILL / WFHL code and name variants.
 */
export function leaveUsesDischargeDateForRMDAForm(
  leaveTypeLabel: string,
  wfhlTranslatedLabel: string = 'WFHL',
  absenceTypeCode?: string | null,
  absenceTypeName?: string | null
): boolean {
  if (leaveTypeLabel === wfhlTranslatedLabel) return true;
  const code = String(absenceTypeCode ?? '').toUpperCase().trim();
  const labelCompact = String(leaveTypeLabel ?? '').replace(/\s+/g, '').toUpperCase();
  if (code === 'WRILL' || code === 'WFHL') return true;
  if (labelCompact === 'WRILL' || labelCompact === 'WRIIL' || labelCompact === 'WFHL') return true;
  const nameFallback = absenceTypeName ?? leaveTypeLabel;
  return absenceTypeIndicatesRmddaDischarge(absenceTypeCode, nameFallback);
}

/**
 * Converts absence hours (e.g. "8:00" or "4:30") to decimal string format for ABRST (e.g. "8.00" or "4.50")
 */
export function absenceHoursToABRST(value: string): string {
  if (!value || typeof value !== 'string') return '0.00';
  const parts = value.trim().split(':');
  const hours = Math.floor(Number(parts[0]) || 0);
  const mins = Number(parts[1]) || 0;
  const decimal = hours + mins / 60;
  return decimal.toFixed(2);
}

/**
 * Converts decimal hours string (e.g. "8.00") back to H:MM format (e.g. "8:00")
 */
export function decimalHoursToTimeString(decimalStr: string | number): string {
  const num = typeof decimalStr === 'number' ? decimalStr : parseFloat(decimalStr);
  if (isNaN(num) || num <= 0) return '0:00';
  const hours = Math.floor(num);
  const mins = Math.round((num - hours) * 60);
  return `${hours}:${String(mins).padStart(2, '0')}`;
}

/**
 * Format a Date, string, or null/undefined to DD-MM-YYYY format expected by SAP API
 */
export function formatDateToAPI(date: Date | string | null | undefined): string {
  if (!date) return '';
  if (typeof date === 'string') {
    // If format is YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date.split('-').reverse().join('-');
    }
    // If format is already DD-MM-YYYY
    if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
      return date;
    }
    const d = new Date(date);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    }
    return date;
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

/**
 * Format a date string (YYYY-MM-DD or DD-MM-YYYY) for friendly human display (e.g. 14 Oct 2026)
 */
export function formatDateDisplay(date: Date | string | null | undefined): string {
  if (!date) return '';
  let d: Date;
  if (typeof date === 'string') {
    if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
      const [dd, mm, yyyy] = date.split('-');
      d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [yyyy, mm, dd] = date.split('-');
      d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    } else {
      d = new Date(date);
    }
  } else {
    d = date;
  }
  if (isNaN(d.getTime())) return String(date);
  const day = String(d.getDate()).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Returns tomorrow's date formatted as YYYY-MM-DD (suitable for HTML date inputs)
 */
export function getTomorrowDateString(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const day = String(tomorrow.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function compactAlphaNum(s: string): string {
  return String(s ?? '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();
}

/**
 * Summary line for leave balance units consumed (matching mobile getLeaveSummaryUsedQuotaDisplay)
 */
export function getLeaveSummaryUsedQuotaDisplay(params: {
  leaveType: string;
  leaveDays: number;
  absenceTypeCode?: string | null;
}): string | null {
  const raw = String(params.leaveType ?? '').trim();
  const comp = compactAlphaNum(raw);
  const code = String(params.absenceTypeCode ?? '').trim().toUpperCase();
  const calendarDays = Math.max(
    1,
    Number.isFinite(Number(params.leaveDays)) ? Number(params.leaveDays) : 1
  );

  // Leave Half Ave Pay FP — 2 balance units per calendar day
  if (
    comp.includes('halfave') &&
    (comp.includes('payfp') || comp.endsWith('fp')) &&
    !comp.includes('cofp')
  ) {
    const amount = (2 * calendarDays).toFixed(2);
    return `Used: ${amount} Days (Balance consumed: 2 units per calendar day)`;
  }

  // Leave Half Ave. Pay half — 1 unit per calendar day
  if (
    /\bpay\s*half\b/i.test(raw) ||
    /half\s*ave\.?\s*pay\s*half/i.test(raw) ||
    (comp.includes('halfavepay') &&
      comp.endsWith('half') &&
      !comp.includes('fp') &&
      !comp.includes('cofp'))
  ) {
    const amount = (1 * calendarDays).toFixed(2);
    return `Used: ${amount} Days`;
  }

  // Casual Leave (not Special CL)
  if (
    (comp === 'casualleave' || code === 'CASL') &&
    !comp.includes('special')
  ) {
    const amount = (1 * calendarDays).toFixed(2);
    return `Used: ${amount} Days`;
  }

  // Earned Leave-Regular or Non-Encash
  if (
    comp.includes('earnedleave') &&
    (comp.includes('regular') || comp.includes('nonencash')) &&
    !comp.includes('encashable')
  ) {
    const amount = (1 * calendarDays).toFixed(2);
    return `Used: ${amount} Days`;
  }

  return null;
}

/**
 * Alerts for specific leave types (matching mobile getAlerts)
 */
export function getAlertsForLeaveType(
  leaveType: string,
  leaveDays: number = 1
): Array<{ message: string; type?: 'info' | 'warning' | 'danger' }> {
  const comp = compactAlphaNum(leaveType);
  const alerts: Array<{ message: string; type?: 'info' | 'warning' | 'danger' }> = [];

  if (comp.includes('earnedleave') && leaveDays > 1) {
    alerts.push({
      message: 'Earned Leave for more than 1 day requires prior intimation / approval.',
      type: 'warning',
    });
  }

  if (comp.includes('outstation')) {
    alerts.push(
      { message: 'Out station leave period must cover the entire non-working period.', type: 'info' },
      { message: 'Last day off must be specified when traveling out of station.', type: 'info' },
      { message: 'First day off must be specified when traveling out of station.', type: 'info' }
    );
  }

  if (comp.includes('paternity')) {
    alerts.push({
      message: 'Paternity leave can be availed within the stipulated period from date of delivery.',
      type: 'info',
    });
  }

  if (comp.includes('blooddonation')) {
    alerts.push(
      { message: 'Special Casual Leave for blood donation covers the day of donation.', type: 'info' },
      { message: 'Please attach donor certificate or official hospital receipt.', type: 'warning' }
    );
  }

  return alerts;
}

/** Internal SAP keys that should not be shown on additional fields cards/summaries */
export const LEAVE_ADDITIONAL_FIELDS_HIDDEN_KEYS: string[] = [
  'OBJ_ID',
  'ORDER_ID',
  'COMP_CODE',
  'COSTCENTER',
  'OBJECT_TYPE',
  'WBS_ELEMENT',
  'PBEG1',
  'PBEG2',
  'PEND1',
  'PEND2',
  'PBEZ1',
  'PBEZ2',
  'PUNB1',
  'PUNB2',
  'ABRTG',
  'ABWTG',
  'ANRTG',
  'KALTG',
  'KENN1',
  'KENN2',
  'ALLDF',
];

/** Friendly human labels for known SAP customer/additional fields */
export const KNOWN_LEAVE_FIELD_LABELS: Record<string, string> = {
  ABRST: 'Absence Hours',
  CUSTOMER01: 'Reason for Leave',
  CUSTOMER02: 'Location of Accident',
  CUSTOMER03: 'Address During Leave',
  CUSTOMER05: 'City / District',
  CUSTOMER06: 'Hospitalization Status',
  CUSTOMER07: 'Address Line 3',
  CUSTOMER08: 'Still Hospitalized / Sterilization',
  CUSTOMER09: 'Reason for Leave',
  CUSTOMER10: 'Day Type (Forenoon / Afternoon / Full Day)',
  RMDDA: 'Discharge / Due Date',
};

/**
 * Open/view an attachment using presigned URL if it is an S3 key, or direct link if http
 */
export async function viewLeaveAttachmentFile(keyOrUrl: string) {
  if (!keyOrUrl) {
    return;
  }
  if (keyOrUrl.startsWith('http://') || keyOrUrl.startsWith('https://')) {
    window.open(keyOrUrl, '_blank');
    return;
  }
  try {
    const ApiModule = await import('../../../core/data/redux/utils/apiCall');
    const { API_URLS } = await import('../../../core/data/redux/apis/urls');
    const Api = ApiModule.default;
    const res: any = await Api.post(API_URLS.AWS_BUCKET.GET_PRESIGNED_URL, { key: keyOrUrl });
    const pUrl =
      res?.data?.data?.url ||
      res?.data?.url ||
      res?.data?.presignedUrl ||
      res?.url ||
      res?.data;
    if (pUrl && typeof pUrl === 'string' && (pUrl.startsWith('http://') || pUrl.startsWith('https://'))) {
      window.open(pUrl, '_blank');
    } else {
      window.open(keyOrUrl, '_blank');
    }
  } catch (err) {
    console.error('Failed to open attachment:', err);
    window.open(keyOrUrl, '_blank');
  }
}

/**
 * Parse attachment list from `leave/emp-leave-file-attachment` responses
 * handling all layers of nested data (identical to mobile leaveFileAttachments.ts).
 */
export function parseEmpLeaveAttachmentList(apiData: unknown): any[] {
  if (apiData == null) return [];
  if (Array.isArray(apiData)) return apiData;
  const layer = apiData as { data?: unknown };
  const inner = layer.data;
  if (Array.isArray(inner)) return inner;
  const innerObj = inner as { data?: unknown } | undefined;
  if (innerObj != null && Array.isArray(innerObj.data)) return innerObj.data as any[];
  const deepObj = innerObj as { data?: { data?: unknown } } | undefined;
  if (deepObj?.data != null && Array.isArray(deepObj.data.data)) return deepObj.data.data as any[];
  return [];
}

/**
 * Map attachment row for UI display and viewing (identical to mobile leaveFileAttachments.ts).
 */
export function normalizeLeaveAttachmentForUi(item: any): any {
  const url = typeof item?.url === 'string' ? item.url : '';
  const fileContent = typeof item?.FileContent === 'string' ? item.FileContent : '';
  const legacyContent = typeof item?.Content === 'string' ? item.Content : '';
  const openUrl = url || legacyContent || fileContent || '';
  const fileName = item?.FileName || item?.file_name || item?.fileName || 'Document';
  let fileSizeKb: string | undefined;
  const descr = item?.FileSizeDescr;
  if (descr) {
    const n = Number(String(descr).replace(/[^\d.]/g, ''));
    if (!Number.isNaN(n) && n > 0) fileSizeKb = String(Math.round(n));
  }
  const padded = item?.FileSize;
  if (fileSizeKb == null && padded != null && /^\d+$/.test(String(padded))) {
    const bytes = Number(String(padded).replace(/^0+/, '') || '0');
    if (bytes > 0) fileSizeKb = String(Math.max(1, Math.round(bytes / 1024)));
  }

  return {
    ...item,
    fileName,
    FileName: fileName,
    file_name: item?.file_name || fileName,
    Content: openUrl,
    url: openUrl,
    ...(fileSizeKb ? { fileSize: fileSizeKb } : {}),
  };
}

